/**
 * Customer Notebook Service
 *
 * Per-tenant running analyst notes (dbo.TenantNotes) that are NOT tied to a
 * single incident. Internal only — these never cross into axncustomerdb.
 *
 * API endpoints (func-axn-analyst-api):
 * - GET    /api/tenants/:tenantId/notes
 * - POST   /api/tenants/:tenantId/notes
 * - PATCH  /api/tenants/:tenantId/notes/:noteId
 * - DELETE /api/tenants/:tenantId/notes/:noteId
 */

import type { TenantNote, NewTenantNote, TenantNoteCategory } from '../data/types';
import type { BadgeVariant } from '../components/ui/Badge';
import { apiFetch, apiPost, apiPatch, apiDelete, type ApiResponse } from './api';

// ============================================================================
// API Response Types
// ============================================================================

interface TenantNotesResponse {
  notes: TenantNote[];
}

interface TenantNoteResponse {
  note: TenantNote;
}

// ============================================================================
// CRUD
// ============================================================================

export async function getTenantNotes(tenantId: string): Promise<ApiResponse<TenantNote[]>> {
  const result = await apiFetch<TenantNotesResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/notes`
  );
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }
  return { data: result.data.notes, success: true };
}

export async function createTenantNote(
  tenantId: string,
  note: NewTenantNote
): Promise<ApiResponse<TenantNote>> {
  const result = await apiPost<TenantNoteResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/notes`,
    {
      content: note.content,
      category: note.category ?? null,
      incidentId: note.incidentId ?? null,
    }
  );
  if (!result.success) {
    return { data: null as unknown as TenantNote, success: false, error: result.error };
  }
  return { data: result.data.note, success: true };
}

export async function updateTenantNote(
  tenantId: string,
  noteId: string,
  patch: { content?: string; category?: TenantNoteCategory | null; isPinned?: boolean }
): Promise<ApiResponse<TenantNote>> {
  const result = await apiPatch<TenantNoteResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/notes/${encodeURIComponent(noteId)}`,
    patch
  );
  if (!result.success) {
    return { data: null as unknown as TenantNote, success: false, error: result.error };
  }
  return { data: result.data.note, success: true };
}

export async function deleteTenantNote(
  tenantId: string,
  noteId: string
): Promise<ApiResponse<void>> {
  const result = await apiDelete<{ deleted: boolean }>(
    `/tenants/${encodeURIComponent(tenantId)}/notes/${encodeURIComponent(noteId)}`
  );
  if (!result.success) {
    return { data: undefined, success: false, error: result.error };
  }
  return { data: undefined, success: true };
}

// ============================================================================
// CATEGORY PRESENTATION
// ============================================================================

export const TENANT_NOTE_CATEGORY_OPTIONS: Array<{ value: TenantNoteCategory; label: string }> = [
  { value: 'General', label: 'General' },
  { value: 'ThreatHunt', label: 'Threat Hunt' },
  { value: 'Tuning', label: 'Tuning' },
  { value: 'CustomerComms', label: 'Customer Comms' },
  { value: 'ProactiveWork', label: 'Proactive Work' },
  { value: 'FollowUp', label: 'Follow-up' },
];

export const TENANT_NOTE_CATEGORY_LABELS: Record<TenantNoteCategory, string> =
  Object.fromEntries(
    TENANT_NOTE_CATEGORY_OPTIONS.map((o) => [o.value, o.label])
  ) as Record<TenantNoteCategory, string>;

export function getTenantNoteCategoryVariant(category: TenantNoteCategory | null): BadgeVariant {
  switch (category) {
    case 'ThreatHunt':
      return 'purple';
    case 'Tuning':
      return 'blue';
    case 'CustomerComms':
      return 'green';
    case 'ProactiveWork':
      return 'orange';
    case 'FollowUp':
      return 'yellow';
    default:
      return 'default';
  }
}
